import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, IconButton } from '@mui/material';
import utc from 'dayjs/plugin/utc';

import Info from '../../Components/Info';
import PageHeading from '../../Components/PageHaeding';
import SectionContainer from '../../Components/SectionContainer';
import Textarea from '../../Components/Textarea';
import CustomButton from '../../Components/CustomButton';
import WarningDialog from '../../Components/WarningDialog';
import InputField from '../../Components/Input';
import { ImageCropperInput } from '../../Components/ImageCropperInput';
import NoData from '../../Components/Nodata';
import Loading from '../../Components/Loading';
import BreadcrumNested from '../../Components/BreadcrumNested';

import { CreateStudioFormSchema } from '../../Utils/yupvalidations';
import { notify } from '../../Utils/toastify';
import Images from '../../Utils/images';
import { useFormErrorListener } from '../../Utils/formErrorHook';
import { ROUTES } from '../../helpers/contants';
import { COUNTRY_LIST } from '../../assets/json/country_dial_info';
import { useAnalytics } from '../../hooks/useAnalytics';
import { ANALYTICS_EVENTS } from '../../helpers/analyticsEvents';

import {
  addStudio,
  getStudioDetail,
  removeContactsList,
  setStudioFormDetails,
  updateStudio,
  updateStudioFormDetails,
} from '../../redux/StudioSlice';
import {
  removeBreadCrumbs,
  setBreadCrumbs,
  setSelectedEntity,
  setSelectedEntityType,
  updateBreadcrumbsMethod,
} from '../../redux/commonSlice';

import ContactsTable from './ContactsTable';
import Address from './Address';

dayjs.extend(utc);

export default function CreateProduction() {
  const location = useLocation();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const [searchParamns] = useSearchParams();
  const productionId = searchParamns.get('productionId');
  const { track } = useAnalytics();

  const from = location.state?.from; // this will be 'create-production' if passed
  const { id }: any = useParams();

  const apiLockRef = useRef<{ [key: string]: boolean }>({});
  const formRef = useRef<HTMLFormElement>(null);

  const [base64File, setBase64File] = useState('');
  const [load, setLoad] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [document, setDocument] = useState('');
  const [department, setDepartment] = React.useState<any>({});

  const [disabled, setDisabled] = useState(false);
  const [openwarn, setOpenwarn] = useState(false);

  const [open, setOpen] = useState(false);
  const [submitStauts, setSubmitStatus] = useState(false);

  const { loading } = useSelector((state: any) => state?.production);

  const { contacts, studioFormData } = useSelector(
    (state: any) => state?.studio,
  );
  const { breadcrumbs, selectedEntity } = useSelector(
    (state: any) => state?.common,
  );
  // updateBreadcrumbsMethod

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    getValues,
    clearErrors,
    trigger,
  } = useForm({
    resolver: yupResolver(CreateStudioFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      schedule: [
        {
          startDate: '',
          scheduleType: null,
          endDate: '',
        },
      ],
      address: null,
      clients: [
        {
          firstName: '',
          lastName: '',
          phoneNo: '',
          email: '',
          countryCode: {
            name: 'United States',
            flag: '🇺🇸',
            code: 'US',
            dial_code: '+1',
          },
          isDisabled: false,
        },
      ],
    },
  });

  // const addressWatch

  const {
    fields: documentFields,
    append: appendDocuments,
    remove: removeDocuments,
  } = useFieldArray({
    control,
    name: 'documents', // Name of the field array
  });

  const handleAddDocument = (docDetails: {
    deptId: string;
    docName: string;
    type: string;
    url: string;
  }) => {
    appendDocuments(docDetails); // Add the new document to the documents array
  };

  useEffect(() => {
    if (id) {
      getStudioDetail(id, (data: any) => {
        Object.entries(data || {}).forEach(([key, value]: any) => {
          if (key === 'image') {
            setBase64File(value);
            return;
          }

          if (key === 'clients') {
            if (value.length) {
              const updateClients = value.map((item: any) => ({
                _id: item._id,
                email: item.email,
                firstName: item.firstName,
                lastName: item.lastName,
                phoneNo: item.phoneNo,
                countryCode: COUNTRY_LIST?.find(
                  (country: any) =>
                    country.dial_code === item?.countryCode &&
                    country.code === item?.countryAbbreviation,
                ),
              }));

              setValue(key, updateClients);
              return;
            } else {
              setValue(key, [
                {
                  firstName: '',
                  lastName: '',
                  phoneNo: '',
                  email: '',
                  countryCode: {
                    name: 'United States',
                    flag: '🇺🇸',
                    code: 'US',
                    dial_code: '+1',
                  },
                  isDisabled: false,
                },
              ]);
              return;
            }
          }

          if (key === 'address') {
            // console.log('=>>>>>>>>>>', value)
            setValue('address', {
              description: value?.address || value?.locationName,
            });
            setValue('zipCode', value?.zipCode);
            setValue('locationName', value?.locationName);
            setValue('country', value?.country);
            setValue('city', value?.city);
            setValue('state', value?.state);
            setValue('addressLine2', value?.addressLine2);
            setValue('addressLine1', value?.address);
            setValue(
              'latitude',
              data?.location?.coordinates[1] || data?.address?.latitude,
            );
            setValue(
              'longitude',
              data?.location?.coordinates[0] || data?.address?.longitude,
            );

            trigger('address');
            return;
          }

          setValue(key, value);
        });
      });
    }
    if (studioFormData) {
      // Populate all form fields with formData
      Object.entries(studioFormData || {}).forEach(([key, value]: any) => {
        if (key === 'image') {
          setBase64File(value);
          return;
        }

        if (key === 'address') {
          // console.log('=>>>>>>>>>>', value)
          if (value) setValue('address', { description: value?.description });
          // setValue('zipCode', value?.zipCode)
          setValue('country', value?.country);
          // setValue('city', value?.city)
          // setValue('state', value?.state)
          return;
        }

        setValue(key, value);
      });
    }

    return () => {
      // updateStudioFormDetails({});
      // removeContactsList([]);
      // updateContactsList([]);
    };
  }, []);

  const onSubmit = (data: any) => {
    // if (apiLockRef.current['submit']) {
    //   // console.log(`API Locked for ${'submit'}`)
    //   return;
    // }

    apiLockRef.current['submit'] = true; // 🔒 Lock API

    /**
     * Reset breadcrumbs and create only:
     * 1. Production Dashboard
     * 2. Production Details
     */
    const updateProductionBreadcrumbs = (
      studioId: string,
      studioName: string,
    ) => {
      const redirectionData = [
        {
          name: 'studio Dashboard',
          type: 'dashboard',
          title: studioName,
          path: `${ROUTES.STUDIO_DASHBOARD}?${studioId}`,
        },
        {
          name: 'studio Details',
          type: 'details',
          title: studioName,
          path: `${ROUTES.STUDIO_DETAIL}/${studioId}`,
        },
      ];

      // Remove all existing breadcrumbs
      removeBreadCrumbs();

      // Add only Studio Dashboard and Studio Details
      setBreadCrumbs(redirectionData);
    };
    const filterNonEmptyValues = (arr: any) => {
      return arr?.filter((item: any) => {
        return (
          item.firstName !== '' && item.lastName !== '' && item.email !== ''
        );
      });
    };

    // console.log('Final Payload =>>>>>', finalPayload)
    if (id) {
      const updatePayload = {
        name: data.name,

        address: {
          // locationName: data.address?.description,
          locationName: data?.locationName,
          city: data.city,
          country: data.country,
          zipCode: data.zipCode,
          state: data.state,
          latitude: data?.latitude,
          longitude: data?.longitude,
          address: data?.addressLine1,
          ...(data?.addressLine2 && { addressLine2: data.addressLine2 }),
        },

        image: base64File,
        description: data.description,
      };
      console.log('=>>FinalPayload ', selectedEntity);
      setSubmitStatus(true);

      // const updatedBreadcrumbs = breadcrumbs?.map((item: any) => {
      //   if (item.path.includes('studio')) {
      //     return {
      //       ...item,
      //       title: data.name,
      //     };
      //   }
      //   return item;
      // });

      if (selectedEntity?._id === id) {
        dispatch(setSelectedEntity({ ...updatePayload, _id: id }));
      }

      // updateBreadcrumbsMethod(updatedBreadcrumbs);

      dispatch(
        updateStudio(id, updatePayload, () => {
          apiLockRef.current['submit'] = false; // 🔓 Unlock API
          updateProductionBreadcrumbs(id, data?.name);
          removeContactsList([]);
          setBase64File('');
          setSubmitStatus(false);
          navigate(`${ROUTES.STUDIO_DETAIL}/${id}`);
        }),
      );
    } else {
      const finalPayload = {
        name: data.name,
        address: {
          locationName: data.locationName,
          city: data.city,
          country: data.country,
          zipCode: data.zipCode,
          state: data.state,
          latitude: data?.latitude,
          longitude: data?.longitude,
          address: data?.addressLine1,
          ...(data?.addressLine2 && { addressLine2: data.addressLine2 }),
        },
        ...(data.studio?.length && {
          studio: data.studio.map((item: any) => item._id),
        }),
        ...(filterNonEmptyValues(data?.clients)?.length && {
          clients: filterNonEmptyValues(data.clients)?.map(
            ({
              firstName,
              lastName,
              phoneNo,
              email,
              countryCode: { dial_code, name, code },
            }: any) => ({
              firstName,
              lastName,
              phoneNo,
              email,
              country: name,
              countryCode: dial_code,
              countryAbbreviation: code,
            }),
          ),
        }),
        ...(base64File && { image: base64File }),
        ...(data?.description && { description: data?.description }),
        ...(documentFields?.length && {
          documents: documentFields?.map(
            ({ docName, docFormat, documentUrl, deptId }) => ({
              docName,
              docFormat,
              documentUrl,
              deptId,
            }),
          ),
        }),
        contacts: contacts?.map(
          ({ baseRole, profilePicture, ...item }: any) => ({
            ...item,
          }),
        ),
      };

      setSubmitStatus(true);

      dispatch(
        addStudio(finalPayload, (data?: any) => {
          apiLockRef.current['submit'] = false; // 🔓 Unlock API
          if (data) {
            setSubmitStatus(false);
            track(ANALYTICS_EVENTS.STUDIO_CREATED, {
              studio_id: data?._id,
              studio_name: data?.name,
            });
            notify('Studio added successfully', 'success');
            removeContactsList([]);
            setBase64File('');
            updateProductionBreadcrumbs(data?._id, data?.name);
            if (from) {
              // Store the new studio temporarily
              localStorage.setItem('newStudio', JSON.stringify(data));
              if (productionId) {
                // Redirect back to create-production page
                navigate(`${ROUTES.CREATE_PRODUCTION}/${productionId}`);
              } else {
                // Redirect back to create-production page
                navigate(`${ROUTES.CREATE_PRODUCTION}`);
              }
            } else {
              dispatch(setSelectedEntity(data));
              dispatch(setSelectedEntityType('studio'));
              navigate(`${ROUTES.STUDIO_DETAIL}/${data?._id}`);
            }
          }
        }),
      );

      dispatch(setStudioFormDetails({}));
    }
  };

  // console.log('=>>>GETTTTVALUEs =>', getValues());
  useFormErrorListener(formRef, errors);

  if (loading && id) {
    return <Loading type="div" pageLoading />;
  }

  console.log(getValues(), 'wklnkjbwfkj');

  return (
    <>
      <div className="pageBody">
        <PageHeading
          // navigateUrl={-1}
          pageTitle={id ? 'Edit Studio' : 'Create Studio'}
          pageName={id ? 'Edit Studio' : 'Create Studio'}
          isNestedBreadcrum
          breadcrumNested={
            <BreadcrumNested
              breadcrums={[
                { name: 'Studio Management', url: -1 },
                {
                  name: id ? 'Edit Studio' : 'Create Studio',
                  url: '',
                },
              ]}
            />
          }
        />
        <div className="formScroller lg-inputs">
          <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
            <div>
              <SectionContainer
                heading="Studio Detail"
                children={
                  <div className="formContainer">
                    <Grid container spacing={2}>
                      <Grid item md={4} xs={12}>
                        <InputField
                          requiredField
                          id="name"
                          label="Studio Name"
                          name="name"
                          placeholder="Enter studio name"
                          control={control}
                          maxLength={50}
                          error={!!errors['name']}
                          helperText={
                            !!errors['name'] ? errors['name'].message : ''
                          }
                        />
                      </Grid>

                      <Grid item md={12} xs={12} className="chat_count">
                        <Textarea
                          id="description"
                          name="description"
                          placeholder="Description"
                          label="Description"
                          rows={5}
                          charLimit={1000}
                          // requiredField
                          control={control}
                          error={!!errors['description']}
                          helperText={
                            !!errors['description']
                              ? errors['description'].message
                              : ''
                          }
                        />
                        {watch('description')?.length ? (
                          <div
                            className={`hint_text  ${
                              !!errors['description'] ? 'hint_has_error' : null
                            }`}
                          >
                            <span>
                              {watch('description')?.length + '/1000'}
                            </span>
                          </div>
                        ) : null}
                      </Grid>
                    </Grid>
                    {/* Shedule Section */}

                    {/* Shedule Section */}
                  </div>
                }
              />

              <SectionContainer
                heading="Studio Address"
                children={
                  <Address
                    control={control}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    // addButton={!state?.editProduction || state?.isAdmin}
                    getValues={getValues}
                    clearErrors={clearErrors}
                    trigger={trigger}
                  />
                }
              />

              {!id && (
                <SectionContainer
                  heading="Add Contacts"
                  children={
                    <>
                      {contacts?.length ? (
                        <>
                          <div className="flex end mb_15">
                            <CustomButton
                              size="large"
                              variant="contained"
                              // text="Create"
                              text="Add"
                              width="auto"
                              // type="submit"
                              disabled={disabled}
                              id="drafted"
                              showIcon={true}
                              icon={Images.Plus_White}
                              iconPosition="start"
                              onClick={() => {
                                updateStudioFormDetails(
                                  {
                                    ...getValues(),
                                    ...(base64File && { image: base64File }),
                                    ...(documentFields && {
                                      documents: documentFields,
                                    }),
                                  },
                                  () => {
                                    navigate(
                                      ROUTES?.ADD_CONTACT_ROLES_STUDIO +
                                        '?isCreation=true',
                                    );
                                  },
                                );
                              }}
                            />
                          </div>

                          <ContactsTable data={contacts} />
                        </>
                      ) : (
                        <NoData
                          heading="No contacts added yet!"
                          title="You have not added any contacts yet."
                          buttonText="Add"
                          handleClick={() => {
                            updateStudioFormDetails(
                              {
                                ...getValues(),
                                ...(base64File && { image: base64File }),
                                ...(documentFields && {
                                  documents: documentFields,
                                }),
                              },
                              () => {
                                navigate(
                                  ROUTES?.ADD_CONTACT_ROLES_STUDIO +
                                    '?isCreation=true',
                                );
                              },
                            );
                          }}
                          showIconButton
                          buttonIcon={Images.Plus_White}
                          // className='add_contact_episode'
                        />
                      )}
                    </>
                  }
                />
              )}

              {base64File != undefined && (
                <SectionContainer
                  heading="Attach Studio Image"
                  children={
                    <div className="formContainer">
                      <Grid container spacing={2} mb={2.2}>
                        <Grid item md={12} xs={12}>
                          <Info
                            theme="light"
                            infoText="Upload this image in a 2:1 aspect ratio as it will be displayed on the studio detail page as a feature graphic."
                          />
                        </Grid>
                      </Grid>

                      <div className="imageContainer">
                        {!base64File && (
                          <ImageCropperInput
                            setLoad={setLoad}
                            setProgress={setUploadProgress}
                            setUrl={setBase64File}
                            loading={load}
                            ratio={2 / 1}
                            title="Add Image"
                            // descTxt="or drag and drop here"
                            onlyImage={true}
                          />
                        )}
                        {base64File && (
                          <div className="">
                            <div className="imageItem ">
                              <Box className="selectimg_video">
                                {/* {base64File?.length > 80 && ( */}

                                <IconButton
                                  className="removeImage"
                                  onClick={() => {
                                    setBase64File('');

                                    // reset({ file: '' })
                                  }}
                                >
                                  <CloseIcon />
                                </IconButton>

                                {/* )} */}
                                <figure className="selected">
                                  <img src={base64File} alt={'more'} />
                                </figure>
                              </Box>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  }
                />
              )}

              {!id && (
                <SectionContainer
                  heading="Attach Document"
                  children={
                    <div className="formContainer">
                      <Grid container spacing={2} mb={2.2}>
                        <Grid item md={12} xs={12}></Grid>
                      </Grid>

                      <div className="imageContainer">
                        {documentFields.length < 20 ? (
                          <ImageCropperInput
                            setLoad={setLoad}
                            setProgress={setUploadProgress}
                            setUrl={setDocument}
                            loading={load}
                            ratio={2 / 1}
                            title="Add Document"
                            type="Document"
                            //   descTxt="or drag and drop here"
                            setDepartment={setDepartment}
                            onAddDocument={handleAddDocument}
                            guideline={true}
                            isCreateStudio
                          />
                        ) : (
                          <p className="MuiTypography-body2 text-error_600">
                            Maximum of 20 documents can be uploaded.
                          </p>
                        )}
                      </div>

                      <div className="doc-cards mt-12">
                        <Grid container spacing={2}>
                          {documentFields.map((item: any, index: number) => {
                            const fileExtension = item?.documentUrl
                              ?.split('.')
                              .pop()
                              ?.toLowerCase();
                            const isImage = [
                              'jpg',
                              'jpeg',
                              'png',
                              'gif',
                              'bmp',
                              'webp',
                            ].includes(fileExtension || '');

                            const isPDF = fileExtension === 'pdf';
                            return (
                              <Grid item md={6} xs={12} key={index}>
                                <div className="doc-card">
                                  <span className="icon">
                                    <i
                                      className={
                                        isPDF
                                          ? 'pdf-icon'
                                          : isImage
                                          ? 'img-icon'
                                          : 'pdf-icon'
                                      }
                                    ></i>
                                  </span>
                                  <p className="MuiTypography-body2 fw600">
                                    {item.docName}
                                  </p>

                                  <button
                                    className="dlt-btn ml-auto"
                                    type="button"
                                    onClick={() => removeDocuments(index)}
                                  >
                                    <i className="delete-icon"></i>
                                  </button>
                                </div>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </div>
                    </div>
                  }
                />
              )}

              <div className="formButtons end">
                <CustomButton
                  onClick={() => {
                    setOpenwarn(true);
                  }}
                  size="large"
                  variant="outlined"
                  text="Cancel"
                  width="auto"
                  type="button"
                  id="cancel"
                  showIcon={false}
                />

                <CustomButton
                  size="large"
                  variant="contained"
                  // text="Create"
                  text={id ? 'Update' : 'Create'}
                  width="auto"
                  type="submit"
                  disabled={id ? false : !isValid}
                  id="drafted"
                  showIcon={false}
                  // onClick={() => {}}
                />
              </div>
            </div>
            <WarningDialog
              open={openwarn}
              // handleClose={() => {
              //   setOpenwarn(false)
              //   navigate(-1)
              // }}
              title="Cancel Studio?"
              description="Are you sure you want to cancel the studio?"
              type="warning"
              handleClose={() => setOpenwarn(false)}
              onClick={() => {
                setOpenwarn(false);
                navigate(-1);
              }}
              cancelBtnText="Cancel"
              confirmBtnText="Yes, Sure"
              showIcon={false}
            />
          </form>
        </div>

        <WarningDialog
          open={open}
          handleClose={() => {
            setOpen;
          }}
          title="Delete"
          description="Do you want to delete this banner?"
          type="warning"
          // onClick={() => {}}
          cancelBtnText="Cancel"
          confirmBtnText="Delete"
          showIcon={false}
        />
      </div>
    </>
  );
}
